// One bill: what's owed and when (and why that date), its invoice file, its details, what it's for (PO lines, a PO's
// deposit or the deposit taken off, a run's cost, a tooling item), the 3-way match with every difference in words
// (accepting them takes a note), and its payments (Wise transfers with their state and receipt, or paid outside).
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, Search, Trash2, Undo2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  BILL_KIND_LABELS,
  BILL_KINDS,
  BILL_LINE_KIND_LABELS,
  BILL_STATUS_HINTS,
  BILL_STATUS_LABELS,
  BILL_STATUS_TONE,
  billDue,
  billFromPo,
  billLinks,
  billMatch,
  billState,
  caseText,
  DIFF_LABELS,
  MATCH_LABELS,
  MATCH_TONE,
  type MatchLine,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_TONE,
  type PoMatch,
  paymentWith,
  poMatch,
  referenceForBill,
  vendorLabelOf,
  WISE_STATE_LABELS,
} from "../../../../shared/bills.ts";
import { numberOf } from "../../../../shared/calc.ts";
import type { BillLine, Payment } from "../../../../shared/db.gen.ts";
import { mulRound } from "../../../../shared/po.ts";
import { COST_CATEGORY_LABELS, type CostCategory } from "../../../../shared/runs.ts";
import { amount, dayOf, postJson, useBillIndex, useCanPay, wiseFeature } from "../../lib/bills.ts";
import { shortDate } from "../../lib/costs.ts";
import { ago, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { Attachments } from "../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select, Textarea } from "../../ui/controls.tsx";
import { CellControls, type Column, DataTable } from "../../ui/DataTable.tsx";
import { Tabs } from "../../ui/PageParts.tsx";
import { type FieldDef, Fields, HistoryList } from "../../ui/RecordPanel.tsx";
import { PaneHead, PaneLink, useInPane } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { Why } from "../../ui/Why.tsx";
import { wiseLink } from "./DueBoard.tsx";
import { Dialog, PaidOutsideDialog, PayDialog } from "./PayDialogs.tsx";
import { WisePicker } from "./WisePicker.tsx";

export function BillPanel({ id, close }: { id: string; close: () => void }) {
  const ix = useBillIndex();
  const bill = ix.billById.get(id);
  const vendors = useRows("vendor");
  const canPay = useCanPay();
  const [tab, setTab] = useState<"bill" | "history">("bill");
  const [dialog, setDialog] = useState<"pay" | "outside" | "find" | null>(null);
  // Side pane --- in the pane, the pane has the frame, the close button and Esc
  const inPane = useInPane();
  const frame = inPane
    ? "flex h-full w-full flex-col bg-surface"
    : "relative z-10 flex h-full w-[min(680px,100vw)] shrink-0 flex-col border-l border-line bg-surface max-[1399px]:absolute max-[1399px]:inset-y-0 max-[1399px]:right-0 max-[1399px]:z-20 max-[1399px]:shadow-pop";
  // Esc closes it (not while a dialog over it is open, or while typing).
  useEffect(() => {
    if (inPane) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.key !== "Escape" || document.querySelector("[data-dialog]") || el?.closest("input, textarea, [role=listbox]")) return;
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, inPane]);
  if (!bill)
    return (
      <aside className={cx(frame, "p-6 text-muted")}>
        That bill isn't here any more.
        <Button className="mt-3 self-start" onClick={close}>
          Close
        </Button>
      </aside>
    );
  const st = billState(ix, id);
  const m = billMatch(ix, id);
  const due = billDue(ix, bill, dayOf);
  const vendor = ix.vendorById.get(bill.vendor_id);
  const hasPayments = st.payments.some((p) => p.payment.status !== "failed" && p.payment.status !== "cancelled");
  const editable = can("bill.update") && (bill.status === "draft" || bill.status === "approved") && !hasPayments;
  const recipient = ix.recipientByVendor.get(bill.vendor_id);
  const fields: FieldDef[] = [
    {
      key: "vendor_id",
      label: "Vendor",
      type: "select",
      options: vendors.filter((v) => !v.archived || v.id === bill.vendor_id).map((v) => ({ value: v.id, label: vendorLabelOf(v) })),
      readOnly: bill.status !== "draft" || hasPayments,
    },
    { key: "invoice_number", label: "Their invoice no.", mono: true, placeholder: "INV-11998, the PI number…", readOnly: !editable },
    { key: "kind", label: "Kind", type: "select", options: BILL_KINDS.map((k) => ({ value: k, label: BILL_KIND_LABELS[k] })), readOnly: !editable },
    { key: "bill_date", label: "Dated", type: "date", readOnly: !editable },
    { key: "due_date", label: "Due", type: "date", placeholder: "Deposits: when acknowledged; balances: before shipping", readOnly: !editable },
    { key: "currency", label: "Currency", mono: true, readOnly: !editable },
    { key: "amount", label: "Amount", type: "number", format: (v) => amount(Number(v ?? 0), bill.currency), readOnly: !editable },
    { key: "description", label: "Description" },
    { key: "notes", label: "Notes", type: "textarea" },
  ];
  return (
    <aside className={frame}>
      {/* Side pane --- in the pane's title bar beside its number (PaneHead); a row of its own off the pane */}
      <PaneHead fallback={(c) => <div className="flex items-start gap-3 border-b border-line px-4 py-3">{c}</div>}>
        <div className={cx("min-w-0 flex-1", inPane && "flex items-center gap-2")}>
          <div className="flex min-w-0 items-center gap-2">
            {!inPane && <span className="font-mono text-[15px] font-semibold">{bill.number}</span>}
            {bill.invoice_number && <span className="truncate font-mono text-muted">{bill.invoice_number}</span>}
          </div>
          <div className="min-w-0 truncate text-muted">
            <PaneLink kind="vendor" id={bill.vendor_id} to={`/vendors?open=${bill.vendor_id}`} className="hover:underline">
              {vendorLabelOf(vendor)}
            </PaneLink>{" "}
            · {BILL_KIND_LABELS[bill.kind]}
            {bill.description ? ` · ${bill.description}` : ""}
          </div>
          <div className={cx("flex shrink-0 flex-wrap gap-1", !inPane && "mt-1.5")}>
            <span title={BILL_STATUS_HINTS[bill.status]}>
              <Badge tone={BILL_STATUS_TONE[bill.status]}>{BILL_STATUS_LABELS[bill.status]}</Badge>
            </span>
            {m.status !== "nothing" && <Badge tone={MATCH_TONE[m.status]}>{MATCH_LABELS[m.status]}</Badge>}
            {bill.source === "wise_history" && <Badge>From Wise history</Badge>}
          </div>
        </div>
        {!inPane && (
          <button type="button" onClick={close} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close (Esc)">
            <X className="size-4" />
          </button>
        )}
      </PaneHead>
      <Tabs
        level="record"
        tabs={[
          { key: "bill", label: "Bill" },
          { key: "history", label: "History" },
        ]}
        active={tab}
        onPick={setTab}
      />
      {tab === "history" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <HistoryList entity="bill" id={bill.id} />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-4 gap-2">
            <Stat label="Amount" value={amount(st.amount, bill.currency)} />
            <Stat label="Paid" value={amount(st.paid, bill.currency)} tone={st.paid > 0 ? "ok" : undefined} />
            <Stat label="On its way" value={amount(st.pending, bill.currency)} tone={st.pending > 0 ? "accent" : undefined} />
            <Stat label="Still to pay" value={amount(st.due, bill.currency)} tone={st.due > 0 && bill.status !== "draft" ? "warn" : undefined} />
          </div>
          <div className="mt-2 text-[12px] text-muted">
            {bill.status === "paid" ? (
              <>Paid {shortDate(bill.paid_on)}.</>
            ) : due.date ? (
              <>
                Due <span className={cx("font-medium", due.date < ix.today ? "text-danger" : "text-text")}>{shortDate(due.date)}</span> · {due.why}
              </>
            ) : (
              due.why
            )}
            {bill.status === "draft" && " · A draft: approve it to put it on the Payments due board."}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {bill.status === "draft" && can("bill.set_status") && (
              <Button variant="primary" icon={<CheckCircle2 className="size-4" />} onClick={() => mutate("bill.set_status", { id, status: "approved" })}>
                Approve
              </Button>
            )}
            {bill.status === "approved" && canPay && st.due > 0 && (
              <Button
                variant="primary"
                onClick={() => setDialog("pay")}
                disabled={!wiseFeature().enabled || !recipient || bill.currency !== "USD"}
                title={
                  !wiseFeature().enabled
                    ? "Wise isn't connected: Settings → Wise"
                    : !recipient
                      ? "Link the vendor to its Wise recipient first (vendor panel)"
                      : bill.currency !== "USD"
                        ? "Wise pays USD only for now"
                        : ""
                }
              >
                Pay with Wise…
              </Button>
            )}
            {(bill.status === "approved" || bill.status === "draft") && canPay && st.due > 0 && (
              <Button onClick={() => setDialog("outside")}>Mark paid outside…</Button>
            )}
            {/* Paid by a Wise transfer HHL Ops already knows (Wise history): pick it here, not only from Wise history */}
            {bill.status !== "void" && bill.status !== "paid" && canPay && st.due > 0 && (
              <Button icon={<Search className="size-3.5" />} onClick={() => setDialog("find")}>
                Find its Wise transfer…
              </Button>
            )}
            {bill.status === "approved" && !hasPayments && can("bill.set_status") && (
              <Button variant="ghost" icon={<Undo2 className="size-3.5" />} onClick={() => mutate("bill.set_status", { id, status: "draft" })}>
                Back to draft
              </Button>
            )}
            {(bill.status === "draft" || bill.status === "approved") && !hasPayments && can("bill.set_status") && (
              <Button
                variant="ghost"
                onClick={() => {
                  const note = prompt("Void this bill (it isn't owed)? Say why:");
                  if (note !== null) mutate("bill.set_status", { id, status: "void", note });
                }}
              >
                Void
              </Button>
            )}
            {bill.status === "void" && can("bill.set_status") && (
              <Button variant="ghost" onClick={() => mutate("bill.set_status", { id, status: "draft" })}>
                Reopen as a draft
              </Button>
            )}
            {bill.status === "draft" && !st.payments.length && can("bill.remove") && (
              <Button
                variant="danger"
                icon={<Trash2 className="size-3.5" />}
                onClick={() => confirm(`Delete ${bill.number} and its files?`) && mutate("bill.remove", { id }).then((ok) => ok && close())}
              >
                Delete
              </Button>
            )}
          </div>
          {!recipient && bill.status !== "paid" && bill.status !== "void" && (
            <div className="mt-2 text-[12px] text-muted">
              No Wise recipient for {vendorLabelOf(vendor)} yet:{" "}
              <PaneLink kind="vendor" id={bill.vendor_id} to={`/vendors?open=${bill.vendor_id}`} className="text-accent-text hover:underline">
                link one on the vendor
              </PaneLink>{" "}
              to pay through Wise.
            </div>
          )}
          {recipient && bill.status === "approved" && (
            <div className="mt-2 text-[12px] text-muted">
              Wise pays {recipient.name}
              {recipient.last4 ? ` ···· ${recipient.last4}` : ""} with the reference <span className="font-mono text-text">{referenceForBill(ix, id)}</span>
            </div>
          )}

          <H3>Invoice</H3>
          <Attachments
            owner={{ type: "bill", id }}
            kind="invoice"
            hint="Drop the vendor's invoice here (PDF or photo), paste a screenshot, or click to choose"
          />

          <H3>Details</H3>
          <div className="flex flex-col gap-0.5">
            <Fields row={bill} fields={fields} update="bill.update" disabled={!can("bill.update")} />
          </div>

          <Lines billId={id} editable={editable} />
          <Match billId={id} />
          <Payments billId={id} canPay={canPay} />
        </div>
      )}
      {dialog === "pay" && <PayDialog billIds={[id]} onClose={() => setDialog(null)} />}
      {dialog === "outside" && <PaidOutsideDialog billIds={[id]} onClose={() => setDialog(null)} />}
      {dialog === "find" && (
        <WisePicker
          target={{
            kind: "bill",
            id,
            vendorId: bill.vendor_id,
            amount: st.due,
            currency: bill.currency,
            date: bill.bill_date ? String(bill.bill_date).slice(0, 10) : null,
            label: `${bill.number}${bill.invoice_number ? ` (${bill.invoice_number})` : ""}`,
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </aside>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" | "accent" }) {
  return (
    <div className="rounded-lg border border-line px-3 py-2">
      <div className="text-[11px] text-muted">{label}</div>
      <div
        className={cx(
          "font-mono text-[14px] font-semibold",
          tone === "ok" && "text-ok",
          tone === "warn" && "text-warn",
          tone === "accent" && "text-accent-text",
        )}
      >
        {value}
      </div>
    </div>
  );
}

const H3 = ({ children, right }: { children: string; right?: React.ReactNode }) => (
  <div className="mt-5 mb-1.5 flex items-center gap-2">
    <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">{children}</h3>
    {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
  </div>
);

function Lines({ billId, editable }: { billId: string; editable: boolean }) {
  const ix = useBillIndex();
  const bill = ix.billById.get(billId);
  const lines = ix.linesByBill.get(billId) ?? [];
  const total = lines.reduce((n, l) => mulRound(n + Number(l.amount), 1, 2), 0);
  const [adding, setAdding] = useState("");
  const vendorPos = useMemo(
    () => ix.d.pos.filter((p) => p.vendor_id === bill?.vendor_id && p.status !== "draft" && p.status !== "cancelled" && p.currency === bill?.currency),
    [ix, bill],
  );
  const freeCosts = ix.d.costs.filter((c) => !ix.linesByCost.has(c.id) && (!c.vendor_id || c.vendor_id === bill?.vendor_id));
  const freeTooling = ix.d.tooling.filter((t) => !ix.linesByTooling.has(t.id) && (!t.vendor_id || t.vendor_id === bill?.vendor_id));
  if (!bill) return null;
  const add = async (value: string) => {
    setAdding("");
    const [kind, target, part] = value.split(":");
    const next = (lines.at(-1)?.position ?? 0) + 1;
    if (kind === "po") {
      const draft = billFromPo(ix, target, (part as "balance" | "received") ?? "balance", dayOf);
      if (draft.problem) return toast.error(draft.problem);
      for (const [i, l] of draft.lines.entries())
        await mutate("bill_line.create", {
          id: crypto.randomUUID(),
          bill_id: billId,
          position: next + i,
          kind: l.kind,
          description: l.description,
          po_id: l.po_id,
          po_line_id: l.po_line_id,
          credit_bill_id: l.credit_bill_id,
          qty: l.qty,
          unit_price: l.unit_price,
          amount: l.amount,
        });
    } else if (kind === "cost") {
      const c = ix.costById.get(target);
      if (!c) return;
      const usd = c.currency === "USD" ? Number(c.amount) : mulRound(Number(c.amount), Number(c.usd_rate), 2);
      await mutate("bill_line.create", {
        id: crypto.randomUUID(),
        bill_id: billId,
        position: next,
        kind: "charge",
        description: c.description || COST_CATEGORY_LABELS[c.category as CostCategory],
        production_cost_id: c.id,
        amount: usd,
      });
    } else if (kind === "tool") {
      const t = ix.toolingById.get(target);
      if (!t) return;
      await mutate("bill_line.create", {
        id: crypto.randomUUID(),
        bill_id: billId,
        position: next,
        kind: "charge",
        description: t.name,
        tooling_item_id: t.id,
        amount: t.currency === "USD" ? Number(t.amount) : mulRound(Number(t.amount), Number(t.usd_rate), 2),
      });
    } else if (kind === "line") {
      await mutate("bill_line.create", { id: crypto.randomUUID(), bill_id: billId, position: next, kind: "other", description: "", amount: 0 });
    }
  };
  const addOptions = [
    { value: "", label: "Add what it's for…" },
    ...vendorPos.flatMap((p) => [
      { value: `po:${p.id}:balance`, label: `${p.number}: every line not billed yet` },
      { value: `po:${p.id}:received`, label: `${p.number}: what's been received and not billed` },
    ]),
    ...freeCosts.map((c) => ({
      value: `cost:${c.id}`,
      label: `Run cost ${ix.runById.get(c.run_id)?.number ?? ""}: ${c.description || COST_CATEGORY_LABELS[c.category as CostCategory]} (${amount(Number(c.amount), c.currency)})`,
    })),
    ...freeTooling.map((t) => ({ value: `tool:${t.id}`, label: `Tooling: ${t.name} (${amount(Number(t.amount), t.currency)})` })),
    { value: "line:", label: "Another line (a charge, a credit)" },
  ];
  const currency = bill.currency;
  const differs = Math.round(total * 100) !== Math.round(Number(bill.amount) * 100);
  // Each line's own fields: goods take a qty and unit price (the amount follows), other lines their amount, a line of
  // kind "other" its description; editing is click to type, Enter saves and moves down.
  const save = (l: BillLine, patch: Partial<Pick<BillLine, "qty" | "unit_price" | "amount" | "description">>) => {
    const qtyN = patch.qty !== undefined ? patch.qty : l.qty;
    const price = patch.unit_price !== undefined ? patch.unit_price : l.unit_price;
    const next = {
      ...patch,
      ...((patch.qty !== undefined || patch.unit_price !== undefined) && qtyN != null && price != null
        ? { amount: mulRound(Number(qtyN), Number(price), 2) }
        : {}),
    };
    mutate("bill_line.update", { id: l.id, patch: next });
  };
  const num = (v: string) => (v.trim() === "" ? null : numberOf(v));
  const columns: Column<BillLine>[] = [
    { key: "position", header: "#", width: "44px", align: "right", cell: (l) => <span className="text-faint">{l.position}</span>, total: () => null },
    {
      key: "kind",
      header: "Kind",
      width: "90px",
      value: (l) => BILL_LINE_KIND_LABELS[l.kind],
      cell: (l) => <span className="text-[12px] text-muted">{BILL_LINE_KIND_LABELS[l.kind]}</span>,
      total: () => null,
    },
    {
      key: "description",
      header: "What",
      width: "minmax(140px,2fr)",
      total: () => <span className="font-normal text-muted">Lines add up to</span>,
      cell: (l) =>
        editable && l.kind === "other" && !l.description ? (
          <span className="text-[12px] text-faint">What it is</span>
        ) : (
          <span className="truncate text-[12px]" title={l.description}>
            {l.description || "—"}
          </span>
        ),
      edit: {
        get: (l) => l.description,
        save: (l, text) => text !== l.description && save(l, { description: text }),
        can: (l) => l.kind === "other",
      },
    },
    {
      key: "for",
      header: "Linked to",
      width: "minmax(110px,1fr)",
      value: (l) => linkedText(ix, l),
      cell: (l) => {
        const po = l.po_id ? ix.poById.get(l.po_id) : undefined;
        return (
          <span className="truncate text-[12px] text-faint">
            {po && (
              <PaneLink kind="po" id={po.id} to={`/pos?open=${po.id}`} className="hover:underline">
                {po.number}
              </PaneLink>
            )}
            {linkedText(ix, l, false)}
          </span>
        );
      },
    },
    {
      key: "qty",
      header: "Qty",
      width: "80px",
      align: "right",
      value: (l) => (l.qty == null ? "" : Number(l.qty)),
      cell: (l) => <span className="font-mono text-[12px]">{l.qty == null ? "" : qty(l.qty)}</span>,
      edit: {
        get: (l) => (l.qty == null ? "" : String(l.qty)),
        save: (l, text) => num(text) !== l.qty && save(l, { qty: num(text) }),
        can: (l) => l.kind === "goods",
      },
    },
    {
      key: "unit_price",
      header: "Unit price",
      width: "100px",
      align: "right",
      value: (l) => (l.unit_price == null ? "" : Number(l.unit_price)),
      text: (l) => (l.unit_price == null ? "" : amount(Number(l.unit_price), currency)),
      cell: (l) => <span className="font-mono text-[12px]">{l.unit_price == null ? "" : amount(Number(l.unit_price), currency)}</span>,
      edit: {
        get: (l) => (l.unit_price == null ? "" : String(l.unit_price)),
        save: (l, text) => num(text) !== l.unit_price && save(l, { unit_price: num(text) }),
        can: (l) => l.kind === "goods",
      },
    },
    {
      key: "amount",
      header: "Amount",
      width: "110px",
      align: "right",
      value: (l) => Number(l.amount),
      text: (l) => amount(Number(l.amount), currency),
      cell: (l) => <span className="font-mono text-[12px]">{amount(Number(l.amount), currency)}</span>,
      edit: {
        get: (l) => String(l.amount),
        save: (l, text) => num(text) !== Number(l.amount) && save(l, { amount: num(text) ?? 0 }),
        can: (l) => l.kind !== "goods",
      },
      // What the lines add up to (every line: a bill's lines are never filtered)
      total: () => <span className="font-mono">{amount(total, currency)}</span>,
    },
    ...(editable
      ? [
          {
            key: "remove",
            header: "",
            width: "40px",
            value: () => null,
            cell: (l: BillLine) => (
              <button
                type="button"
                className="rounded p-0.5 text-faint hover:text-danger"
                title="Remove the line"
                onClick={(e) => {
                  e.stopPropagation();
                  mutate("bill_line.remove", { id: l.id });
                }}
              >
                <Trash2 className="size-3.5" />
              </button>
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <H3
        right={
          <>
            {editable && (
              <Select
                value={adding}
                options={addOptions}
                onChange={(e) => e.target.value && add(e.target.value)}
                className="h-7 w-64 text-[12px]"
                aria-label="Add a line"
              />
            )}
          </>
        }
      >
        What it's for
      </H3>
      {!lines.length ? (
        <div className="rounded-md border border-dashed border-line-strong px-3 py-3 text-[12px] text-muted">
          No lines: it isn't linked to a PO, a run's cost or tooling, so there's nothing to match it with.
          {editable ? " Add what it's for above." : ""}
        </div>
      ) : (
        <>
          <DataTable
            fit
            rows={lines}
            columns={columns}
            editing={editable}
            defaultSort={{ key: "position" }}
            exportName="Bill lines"
            pageFilters={false}
            rowNoun={["line", "lines"]}
          />
          {differs && (
            <div className="mt-1.5 flex items-center gap-2 px-1 text-[12px]">
              <span className="text-muted">Lines add up to</span>
              <span className="font-mono font-medium">{amount(total, currency)}</span>
              <span className="text-warn">the bill says {amount(Number(bill.amount), currency)}</span>
              {editable && (
                <button
                  type="button"
                  className="ml-auto text-accent-text hover:underline"
                  onClick={() => mutate("bill.update", { id: billId, patch: { amount: total } })}
                >
                  Use the lines' total
                </button>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

/** What a line is linked to besides its PO, in words ("· takes off BILL-12", "Run cost on PR-002", "Tooling ledger"); with
 * `po`, its PO's number first (for search and Copy / CSV). */
function linkedText(ix: ReturnType<typeof useBillIndex>, l: BillLine, po = true) {
  const p = po && l.po_id ? ix.poById.get(l.po_id) : undefined;
  const cost = l.production_cost_id ? ix.costById.get(l.production_cost_id) : undefined;
  const credit = l.credit_bill_id ? ix.billById.get(l.credit_bill_id) : undefined;
  return [
    p?.number ?? "",
    credit ? ` · takes off ${credit.number}` : "",
    cost ? `Run cost on ${ix.runById.get(cost.run_id)?.number ?? "a run"}` : "",
    l.tooling_item_id && ix.toolingById.get(l.tooling_item_id) ? "Tooling ledger" : "",
  ].join("");
}

function Match({ billId }: { billId: string }) {
  const ix = useBillIndex();
  const bill = ix.billById.get(billId);
  const m = billMatch(ix, billId);
  const [note, setNote] = useState("");
  if (!bill) return null;
  const poIds = billLinks(ix, billId).poIds;
  // Each PO's lines under a band of its own
  const rows: MatchRow[] = poIds.flatMap((poId) => {
    const pm = poMatch(ix, poId);
    return pm ? pm.lines.map((l) => ({ ...l, id: l.poLine.id, po: pm.po })) : [];
  });
  return (
    <>
      <H3>3-way match</H3>
      <div className="rounded-lg border border-line px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Badge tone={MATCH_TONE[m.status]}>{MATCH_LABELS[m.status]}</Badge>
          <span className="text-[12px] text-muted">{m.text}</span>
        </div>
        {m.diffs.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1 text-[12px]">
            {m.diffs.map((d) => (
              <li key={`${d.kind}:${d.poLineId ?? d.poId ?? ""}:${d.billId ?? ""}`} className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warn" />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{DIFF_LABELS[d.kind]}:</span> {d.text}
                </span>
                <span className={cx("shrink-0 font-mono", d.amount > 0 ? "text-warn" : "text-muted")}>
                  {d.amount > 0 ? "+" : ""}
                  {amount(d.amount, bill.currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {m.waiting.length > 0 && (
          <ul className="mt-2 flex flex-col gap-0.5 text-[12px] text-muted">
            {m.waiting.map((d) => (
              <li key={`w:${d.poLineId}`}>Still coming: {d.text}</li>
            ))}
          </ul>
        )}
        {m.status === "differences" && can("bill.accept_match") && (
          <div className="mt-2.5 flex items-end gap-2">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why it's OK (agreed price change, short shipment credited next PO…)"
              className="min-h-10 flex-1"
            />
            <Button
              disabled={!note.trim()}
              onClick={async () => {
                if (await mutate("bill.accept_match", { id: billId, key: m.key, note: note.trim() })) setNote("");
              }}
            >
              Accept
            </Button>
          </div>
        )}
        {m.status === "accepted" && (
          <div className="mt-2 flex items-center gap-2 text-[12px] text-muted">
            <span>Accepted: "{bill.match_note}"</span>
            {can("bill.clear_match") && (
              <button type="button" className="ml-auto text-accent-text hover:underline" onClick={() => mutate("bill.clear_match", { id: billId })}>
                Take it back
              </button>
            )}
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div className="mt-2">
          <DataTable
            fit
            rows={rows}
            columns={matchColumns}
            sectionOf={(l) => ({
              key: l.po.id,
              order: poIds.indexOf(l.po.id),
              label: (
                <span className="flex items-end gap-2">
                  <PaneLink kind="po" id={l.po.id} to={`/pos?open=${l.po.id}`} className="text-text hover:underline">
                    {l.po.number}
                  </PaneLink>
                  <span className="font-normal normal-case">ordered · received · billed (every bill on it)</span>
                </span>
              ),
            })}
            defaultSort={{ key: "line" }}
            exportName="Bill match"
            pageFilters={false}
            rowNoun={["line", "lines"]}
          />
        </div>
      )}
    </>
  );
}

/** A PO line in a bill's 3-way match: what was ordered, received and billed (every bill on the PO). */
type MatchRow = MatchLine & { id: string; po: PoMatch["po"] };

const matchColumns: Column<MatchRow>[] = [
  {
    key: "line",
    header: "#",
    width: "44px",
    align: "right",
    value: (l) => l.poLine.position,
    cell: (l) => <span className="text-faint">{l.poLine.position}</span>,
  },
  {
    key: "sku",
    header: "SKU",
    width: "minmax(100px,1fr)",
    value: (l) => l.poLine.sku || l.poLine.name,
    cell: (l) => <span className="truncate font-mono text-[12px]">{l.poLine.sku || l.poLine.name}</span>,
  },
  {
    key: "name",
    header: "Name",
    width: "minmax(120px,1fr)",
    optional: true,
    value: (l) => l.poLine.name,
    cell: (l) => <span className="truncate text-[12px] text-muted">{l.poLine.name}</span>,
  },
  { key: "ordered", header: "Ordered", width: "80px", align: "right", cell: (l) => <span className="font-mono text-[12px]">{qty(l.ordered)}</span> },
  {
    key: "received",
    header: "Received",
    width: "86px",
    align: "right",
    cell: (l) => <span className={cx("font-mono text-[12px]", l.received < l.billed && "text-warn")}>{qty(l.received)}</span>,
  },
  {
    key: "billed",
    header: "Billed",
    width: "80px",
    align: "right",
    cell: (l) => <span className={cx("font-mono text-[12px]", l.billed > l.ordered && "text-warn")}>{qty(l.billed)}</span>,
  },
  {
    key: "price",
    header: "PO price",
    width: "100px",
    align: "right",
    value: (l) => l.price ?? "",
    text: (l) => (l.price == null ? "" : amount(l.price, l.po.currency)),
    cell: (l) => (
      <span className="font-mono text-[12px] text-muted">
        <Why
          title={l.poLine.sku}
          lines={l.bills.map((b) => ({ label: `${b.number}: ${qty(b.qty)} ×`, value: b.price == null ? "—" : amount(b.price, l.po.currency) }))}
        >
          {l.price == null ? "—" : amount(l.price, l.po.currency)}
        </Why>
      </span>
    ),
  },
];

function Payments({ billId, canPay }: { billId: string; canPay: boolean }) {
  const ix = useBillIndex();
  const st = billState(ix, billId);
  // A failed payment can be paid again (a new payment) while the bill is approved and owed.
  const owed = ix.billById.get(billId)?.status === "approved" && st.due > 0;
  const [busy, setBusy] = useState("");
  const wise = wiseFeature();
  const users = useRows("app_user");
  // The payment open in its dialog (a row's click)
  const [openId, setOpenId] = useState<string | null>(null);
  if (!st.payments.length) return null;
  const call = async (key: string, path: string, body?: unknown, done?: string) => {
    setBusy(key);
    try {
      const res = await postJson<{ outcome?: string }>(path, body);
      toast.ok(res.outcome ?? done ?? "Done");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy("");
    }
  };
  const rows: PayRow[] = st.payments.map(({ payment: p, amount: share }) => {
    const who = users.find((u) => u.id === p.created_by);
    return {
      ...p,
      share,
      how: paymentWith(ix, p),
      who: who ? who.name || who.email : "",
      receipt: (ix.filesByOwner.get(`payment:${p.id}`) ?? []).length > 0,
      flags: paymentNotes(p, canPay),
    };
  });
  const open = st.payments.find((x) => x.payment.id === openId);
  const columns: Column<PayRow>[] = [
    {
      key: "number",
      header: "Payment",
      width: "92px",
      value: (p) => Number(p.number.replace(/\D/g, "")) || 0,
      text: (p) => p.number,
      cell: (p) => <span className="font-mono text-[12px] font-medium">{p.number}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "150px",
      value: (p) => PAYMENT_STATUS_LABELS[p.status],
      cell: (p) => <Badge tone={PAYMENT_STATUS_TONE[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>,
    },
    { key: "how", header: "How", width: "minmax(100px,1fr)", cell: (p) => <span className="truncate text-[12px] text-muted">{p.how}</span> },
    {
      key: "paid_on",
      header: "Paid",
      width: "90px",
      value: (p) => String(p.paid_on ?? ""),
      cell: (p) => <span className="text-[12px] text-muted">{p.paid_on ? shortDate(p.paid_on) : ""}</span>,
    },
    { key: "who", header: "By", width: "110px", optional: true, cell: (p) => <span className="truncate text-[12px] text-muted">{p.who}</span> },
    {
      key: "created_at",
      header: "Made",
      width: "90px",
      optional: true,
      value: (p) => p.created_at,
      text: (p) => ago(p.created_at),
      cell: (p) => <span className="text-[12px] text-muted">{ago(p.created_at)}</span>,
    },
    {
      key: "share",
      header: "Amount",
      width: "110px",
      align: "right",
      text: (p) => amount(p.share, p.currency),
      cell: (p) => <span className="font-mono font-medium">{amount(p.share, p.currency)}</span>,
    },
    {
      key: "all",
      header: "In all",
      headerTitle: "The whole payment, when it paid other bills too",
      width: "110px",
      align: "right",
      value: (p) => (Number(p.amount) !== p.share ? Number(p.amount) : ""),
      text: (p) => (Number(p.amount) !== p.share ? amount(Number(p.amount), p.currency) : ""),
      cell: (p) =>
        Number(p.amount) !== p.share ? <span className="font-mono text-[12px] text-muted">{amount(Number(p.amount), p.currency)} in all</span> : null,
    },
    {
      key: "reference",
      header: "Reference",
      width: "minmax(120px,1fr)",
      cell: (p) => (
        <span className="truncate font-mono text-[12px]" title={p.reference}>
          {p.reference}
        </span>
      ),
    },
    {
      key: "fee",
      header: "Fee",
      width: "90px",
      align: "right",
      value: (p) => Number(p.fee) || 0,
      text: (p) => (Number(p.fee) > 0 ? amount(Number(p.fee), p.fee_currency || p.currency) : ""),
      cell: (p) => (Number(p.fee) > 0 ? <span className="font-mono text-[12px] text-muted">{amount(Number(p.fee), p.fee_currency || p.currency)}</span> : null),
    },
    {
      key: "wise",
      header: "Wise",
      width: "minmax(150px,1fr)",
      value: (p) => (p.method === "wise" && p.wise_transfer_id ? `Wise ${p.wise_transfer_id} (${WISE_STATE_LABELS[p.wise_state] ?? p.wise_state})` : ""),
      cell: (p) =>
        p.method === "wise" &&
        !!p.wise_transfer_id && (
          <CellControls className="gap-1 text-[12px] text-muted">
            <a
              href={wiseLink(p.wise_env || wise.env, Number(p.wise_transfer_id))}
              target="_blank"
              rel="noreferrer"
              className="inline-flex shrink-0 items-center gap-1 text-accent-text hover:underline"
            >
              Wise {p.wise_transfer_id} <ExternalLink className="size-3" />
            </a>
            <span className="truncate">({WISE_STATE_LABELS[p.wise_state] ?? p.wise_state})</span>
          </CellControls>
        ),
    },
    {
      key: "receipt",
      header: "Receipt",
      width: "90px",
      value: (p) => (p.receipt ? "receipt ✓" : ""),
      cell: (p) => (p.receipt ? <span className="text-[12px] text-muted">receipt ✓</span> : <span className="text-[12px] text-faint">none yet</span>),
    },
    {
      key: "notes",
      header: "Note",
      headerTitle: "What it's waiting for or what went wrong; click the row for everything and what to do",
      width: "minmax(160px,1.5fr)",
      value: (p) => p.flags.map((n) => n.text).join(" · "),
      cell: (p) =>
        p.flags[0] && (
          <span className={cx("truncate text-[12px]", NOTE_TONE[p.flags[0].tone])} title={p.flags.map((n) => n.text).join("\n")}>
            {p.flags[0].text}
            {p.flags.length > 1 && <span className="text-faint"> · +{p.flags.length - 1}</span>}
          </span>
        ),
    },
  ];
  return (
    <>
      <H3>Payments</H3>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "number" }}
        onOpen={(p) => setOpenId(p.id)}
        exportName="Bill payments"
        pageFilters={false}
        rowNoun={["payment", "payments"]}
      />
      {open && (
        <Dialog
          title={`Payment ${open.payment.number}`}
          sub={`${PAYMENT_STATUS_LABELS[open.payment.status]} · ${paymentWith(ix, open.payment)}`}
          onClose={() => setOpenId(null)}
        >
          <div className="mt-3">
            <PaymentCard p={open.payment} share={open.amount} canPay={canPay} busy={busy} call={call} wiseEnv={wise.env} fake={wise.fake} owed={owed} />
          </div>
        </Dialog>
      )}
    </>
  );
}

type PayRow = Payment & { share: number; how: string; who: string; receipt: boolean; flags: PayNote[] };
type PayNote = { text: string; tone: "danger" | "warn" | "accent" };
const NOTE_TONE: Record<PayNote["tone"], string> = { danger: "text-danger", warn: "text-warn", accent: "text-accent-text" };

/** What a payment's card says it's waiting for or what went wrong, worst first, for its row's Note. */
function paymentNotes(p: Payment, canPay: boolean): PayNote[] {
  const cases = (Array.isArray(p.wise_active_cases) ? p.wise_active_cases : []) as string[];
  return [
    ...(p.wise_error ? [{ text: p.wise_error, tone: p.wise_check ? ("warn" as const) : ("danger" as const) }] : []),
    ...(p.wise_check && canPay ? [{ text: "Check in Wise", tone: "warn" as const }] : []),
    ...(p.wise_verify === "pending"
      ? [{ text: "Not checked against Wise yet (Wise didn't answer): HHL Ops checks it within 30 minutes.", tone: "warn" as const }]
      : []),
    ...(p.fee_review ? [{ text: p.fee_review, tone: "warn" as const }] : []),
    ...(cases.length ? [{ text: `Wise: ${cases.map(caseText).join("; ")}`, tone: "warn" as const }] : []),
    ...(p.status === "awaiting_funding"
      ? [{ text: "Waiting for someone to approve and fund it in the Wise app. HHL Ops marks it paid when Wise sends it.", tone: "accent" as const }]
      : []),
    ...(p.receipt_status === "failed" ? [{ text: "Wise's receipt didn't come yet", tone: "warn" as const }] : []),
  ];
}

/**
 * "Check in Wise": Wise's answer was unclear and an hour on it has no transfer for this payment by its id. The bill
 * stays held until someone who can pay says which (POST /api/wise/payments/:id/resolve).
 */
function CheckInWise({ p, busy, call }: { p: Payment; busy: string; call: (key: string, path: string, body?: unknown, done?: string) => Promise<void> }) {
  const [transfer, setTransfer] = useState("");
  const id = /^\d+$/.test(transfer.trim()) ? Number(transfer.trim()) : null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-md border border-warn/40 bg-warn-soft/40 px-2 py-1.5 text-[12px]">
      <span className="font-medium">Check in Wise:</span>
      <Input
        value={transfer}
        inputMode="numeric"
        onChange={(e) => setTransfer(e.target.value)}
        placeholder="Wise transfer no."
        className="h-7 w-40 font-mono text-[12px]"
        aria-label="Wise transfer number"
      />
      <Button
        size="sm"
        disabled={!!busy || !id}
        onClick={() => call(`resolve:${p.id}`, `/api/wise/payments/${p.id}/resolve`, { transfer_id: id }, "Recorded the transfer")}
      >
        It's in Wise
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={!!busy}
        title="HHL Ops looks once more by its id; if Wise still has nothing, this one is failed and the bill is paid again as a new payment"
        onClick={() => call(`resolve:${p.id}`, `/api/wise/payments/${p.id}/resolve`, { pay_again: true }, "Paid again")}
      >
        Not in Wise: pay again
      </Button>
    </div>
  );
}

/** A fee that may be the one someone entered on the run by hand: nothing was added until a person says. */
function FeeReview({ p }: { p: Payment }) {
  const [busy, setBusy] = useState(false);
  const decide = async (add: boolean) => {
    setBusy(true);
    await mutate("payment.fee_decide", { id: p.id, add });
    setBusy(false);
  };
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-md border border-warn/40 bg-warn-soft/40 px-2 py-1.5 text-[12px]">
      <span className="min-w-0 flex-1">{p.fee_review}</span>
      {can("payment.fee_decide") && (
        <>
          <Button size="sm" disabled={busy} onClick={() => decide(false)}>
            Same fee
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide(true)}>
            Add it to the run
          </Button>
        </>
      )}
    </div>
  );
}

function PaymentCard({
  p,
  share,
  canPay,
  busy,
  call,
  wiseEnv,
  fake,
  owed,
}: {
  p: Payment;
  share: number;
  canPay: boolean;
  busy: string;
  call: (key: string, path: string, body?: unknown, done?: string) => Promise<void>;
  wiseEnv: string;
  fake: boolean;
  owed: boolean;
}) {
  const users = useRows("app_user");
  const ix = useBillIndex();
  const who = users.find((u) => u.id === p.created_by);
  const cases = (Array.isArray(p.wise_active_cases) ? p.wise_active_cases : []) as string[];
  const inWise = p.method === "wise" && !!p.wise_transfer_id;
  return (
    <div className="rounded-lg border border-line px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12px] font-medium">{p.number}</span>
        <Badge tone={PAYMENT_STATUS_TONE[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>
        <span className="text-[12px] text-muted">
          {paymentWith(ix, p)}
          {p.paid_on ? ` · paid ${shortDate(p.paid_on)}` : ""}
          {who ? ` · by ${who.name || who.email}` : ""} · {ago(p.created_at)}
        </span>
        <span className="ml-auto font-mono font-medium">{amount(share, p.currency)}</span>
      </div>
      <div className="mt-1 text-[12px] text-muted">
        {p.reference && (
          <>
            Reference <span className="font-mono text-text">{p.reference}</span>
          </>
        )}
        {Number(p.fee) > 0 && ` · fee ${amount(Number(p.fee), p.fee_currency || p.currency)}`}
        {Number(p.amount) !== share && ` · ${amount(Number(p.amount), p.currency)} in all`}
        {inWise && (
          <>
            {" · "}
            <a
              href={wiseLink(p.wise_env || wiseEnv, Number(p.wise_transfer_id))}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-accent-text hover:underline"
            >
              Wise {p.wise_transfer_id} <ExternalLink className="size-3" />
            </a>{" "}
            ({WISE_STATE_LABELS[p.wise_state] ?? p.wise_state})
          </>
        )}
      </div>
      {p.wise_error && <div className={cx("mt-1 text-[12px]", p.wise_check ? "text-warn" : "text-danger")}>{p.wise_error}</div>}
      {p.wise_check && canPay && <CheckInWise p={p} busy={busy} call={call} />}
      {p.wise_verify === "pending" && (
        <div className="mt-1 text-[12px] text-warn">Not checked against Wise yet (Wise didn't answer): HHL Ops checks it within 30 minutes.</div>
      )}
      {p.fee_review && <FeeReview p={p} />}
      {cases.length > 0 && <div className="mt-1 text-[12px] text-warn">Wise: {cases.map(caseText).join("; ")}</div>}
      {p.status === "awaiting_funding" && (
        <div className="mt-1 text-[12px] text-accent-text">
          Waiting for someone to approve and fund it in the Wise app. HHL Ops marks it paid when Wise sends it.
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Attachments owner={{ type: "payment", id: p.id }} compact kind="receipt" />
        {p.receipt_status === "failed" && <span className="text-[11px] text-warn">Wise's receipt didn't come yet</span>}
        <span className="ml-auto flex items-center gap-2">
          {canPay && madeByPay(p) && (p.status === "draft" || (p.status === "failed" && owed)) && (
            <Button
              size="sm"
              disabled={!!busy}
              title={
                p.status === "draft" ? "Asks Wise again with the same id: it can never make a second transfer" : "A new payment, through every check again"
              }
              onClick={() => call(`retry:${p.id}`, `/api/wise/payments/${p.id}/retry`, undefined, p.status === "draft" ? "Asked Wise again" : "Paid again")}
            >
              {p.status === "draft" ? "Retry (safe)" : "Pay again"}
            </Button>
          )}
          {inWise && (p.status === "awaiting_funding" || p.status === "processing") && (
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw className="size-3" />}
              disabled={!!busy}
              onClick={() => call(`refresh:${p.id}`, `/api/wise/payments/${p.id}/refresh`)}
            >
              Check now
            </Button>
          )}
          {inWise && p.status === "paid" && p.receipt_status !== "stored" && (
            <Button
              size="sm"
              variant="ghost"
              disabled={!!busy}
              onClick={() => call(`receipt:${p.id}`, `/api/wise/payments/${p.id}/receipt`, undefined, "Receipt stored")}
            >
              Fetch receipt
            </Button>
          )}
          {fake && inWise && p.status === "awaiting_funding" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => call(`fake:${p.id}`, "/api/wise/fake/advance", { payment_id: p.id, state: "processing" }, "Fake Wise: funded")}
            >
              Fake: fund it
            </Button>
          )}
          {fake && inWise && (p.status === "awaiting_funding" || p.status === "processing") && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => call(`fake2:${p.id}`, "/api/wise/fake/advance", { payment_id: p.id, state: "outgoing_payment_sent" }, "Fake Wise: sent")}
            >
              Fake: send it
            </Button>
          )}
          {canPay && !inWiseHere(p) && (
            <button type="button" className="text-[11px] text-faint hover:text-danger" onClick={() => takeBack(p, ix)}>
              Take back
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * Take back: a payment linked from Wise history undoes what the link made too (bills it made, lines, run costs for
 * extras, prices it set): the confirm says what, and the toast what was undone and what stays.
 */
async function takeBack(p: Payment, ix: ReturnType<typeof useBillIndex>) {
  const made = (p.link_made ?? {}) as {
    transfer?: number;
    bills?: string[];
    approved?: string[];
    lines?: { bill_number: string }[];
    accepted?: { bill_id: string }[];
    costs?: { label: string }[];
    prices?: { po_number: string; label: string }[];
  };
  const number = (id: string) => ix.billById.get(id)?.number ?? "a bill";
  const hasFile = (id: string) => (ix.filesByOwner.get(`bill:${id}`) ?? []).length > 0;
  const undo = made.transfer
    ? [
        ...(made.bills ?? []).map((id) => `${number(id)} it made is ${hasFile(id) ? "voided (it has a file)" : "removed"}`),
        ...[...new Set((made.lines ?? []).map((l) => l.bill_number))].map((n) => `what it added to ${n} comes off`),
        ...(made.accepted ?? []).map((a) => `${number(a.bill_id)}'s accepted differences go back as they were (unless someone has accepted them since)`),
        ...(made.approved ?? []).map((id) => `${number(id)} goes back to a draft (unless another payment is on it)`),
        ...(made.costs ?? []).map((c) => `${c.label} is taken off its run`),
        ...(made.prices ?? []).map((x) => `${x.po_number} ${x.label} has no price again`),
      ]
    : [];
  // Owed again: the bills it paid that it didn't make (those go).
  const madeBills = new Set(made.bills ?? []);
  const owed = [...new Set((ix.allocsByPayment.get(p.id) ?? []).map((a) => a.bill_id))].filter((id) => !madeBills.has(id)).map(number);
  const owedText = owed.length ? ` ${owed.join(", ")} ${owed.length === 1 ? "is" : "are"} owed again.` : "";
  const text = made.transfer
    ? `Take ${p.number} back? It was linked from Wise transfer ${made.transfer}${undo.length ? `: ${undo.join("; ")}` : ""}.${owedText}`
    : `Take ${p.number} back? The bills it paid are owed again.`;
  if (!confirm(text)) return;
  const { ok, result } = await mutateWithResult("payment.remove", { id: p.id });
  const r = (result ?? {}) as { undone?: string[]; kept?: string[] };
  const lines = [...(r.undone ?? []), ...(r.kept ?? [])];
  if (ok && made.transfer) toast.ok(`${p.number} taken back${lines.length ? `: ${lines.join("; ")}` : ""}`);
}

/** Made by Pay here (it always gets a quote), not a Wise payment recorded by hand. */
const madeByPay = (p: Payment) => p.method === "wise" && p.source === "ui" && !!p.wise_quote_id;
/** A transfer HHL Ops made that's in Wise (or may be): taken back only by cancelling it in Wise. */
const inWiseHere = (p: Payment) => madeByPay(p) && ((!!p.wise_transfer_id && p.status !== "failed" && p.status !== "cancelled") || p.status === "draft");
